<template>
  <div class="col-12">
    <div class="entity-actions">
      <entity-actions
        :actions="actions"
        @changed="dispatchAction" />
    </div>
    <hs-table
      v-if="orderedTableItems?.length"
      class="view-list-table"
      :id="tableId"
      ref="viewListRef"
      :allow-selection="true"
      :cast-shadow="false"
      :columns="columns"
      :is-loading="isLoading"
      :items="orderedTableItems"
      :show-filters="false"
      :total-count="totalCount"
      :is-editable="true"
      category="Table > User Views"
      data-row-height="8rem"
      :assign-inline-edit-button-styles="false"
      pagination
      client-side-pagination
      enable-client-side-sorting
      @selection-changed="selectedItems = $event">
      <template #isDefault="{ item }">
        <yes-no-badge :yes="item.view.isDefault" />
      </template>

      <template #isApplied="{ item }">
        <yes-no-badge :yes="item.view.isApplied" />
      </template>

      <template #name="{ item }">
        <span>
          {{ item.view.name }}
        </span>
      </template>

      <template #summary="{ item }">
        <view-filters-summary :summary="item.summary" />
      </template>

      <template #actions="{ item }">
        <div class="actions">
          <hs-button
            class="btn-xs btn-no-text"
            v-tooltip="capitalize(i('actions.apply'))"
            :icon-left="markRaw(IconApplyAll)"
            variant="success"
            @click="() => applyView(item)" />
          <hs-button
            class="btn-xs btn-no-text"
            v-tooltip="capitalize(i(`messages.${item.view.isDefault ? 'unsetDefaultView' : 'setAsDefaultView'}`))"
            :icon-left="markRaw(item.view.isDefault ? IconClose : IconCheck2)"
            variant="tertiary"
            @click="() => markAsDefault(item)" />
          <hs-button
            class="btn-xs btn-no-text"
            v-tooltip="capitalize(i('actions.delete'))"
            :icon-left="markRaw(IconDelete)"
            variant="secondary"
            @click="() => deleteView(item)" />
        </div>
      </template>
    </hs-table>
  </div>
</template>

<script lang="ts" setup>
  import { capitalize, computed, markRaw, onMounted, ref, useTemplateRef } from 'vue';
  import { ComponentExposed } from 'vue-component-type-helpers';

  import { EntityActions } from '@/common/components/controls';
  import { IAction } from '@/common/components/controls/EntityActions.vue';
  import HsTable from '@/common/components/controls/table/Table.vue';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
  import { ITableFilter } from '@/common/components/controls/table/TableFilter';
  import TableSettings, { TableSettingKeys } from '@/common/components/controls/table/TableUserSettings/TableSettings';
  import UserView from '@/common/components/controls/table/TableUserSettings/UserView';
  import ViewFiltersSummary from '@/common/components/controls/table/user-views/ViewFiltersSummary.vue';
  import HsButton from '@/common/components/ui/Button/Button.vue';
  import YesNoBadge from '@/common/components/ui/YesNoBadge.vue';
  import { IconApplyAll, IconCheck2, IconClose, IconDelete, IconFileXLS } from '@/common/icons/monochrome';
  import EntityProperty from '@/common/models/EntityProperty';
  import { capitalizeTranslation, clearTooltips, i, orderBy } from '@/globals';
  import DownloadUtils, { CellType } from '@/utils/DownloadUtils';

  interface ITableItem {
    id: string;
    view: UserView;
    summary: string[];
  }

  interface IProps {
    availableFilters: ITableFilter[];
    tableSettings: TableSettings;
  }

  enum ViewActionType {
    Delete = 'delete',
    Apply = 'apply',
    SetAsDefault = 'setAsDefault',
    ExportXlsx = 'exportXlsx',
  }

  const tableId = 'user_filter_list_table'; // Never change this ID, you'll lose the user's filters

  const { availableFilters, tableSettings } = defineProps<IProps>();

  const emits = defineEmits(['applyView', 'closed']);

  const viewListRef = useTemplateRef<ComponentExposed<typeof HsTable>>('viewListRef');

  const isLoading = ref(false);
  const tableItems = ref<ITableItem[]>([]);
  const selectedItems = ref<ITableItem[]>([]);

  const actions = computed<IAction[]>(() => [
    {
      name: capitalizeTranslation('actions.apply'),
      icon: markRaw(IconApplyAll),
      $isDisabled: selectedItems.value.length !== 1,
      type: ViewActionType.Apply,
    },
    {
      name: capitalizeTranslation('actions.setAsDefaultFilters'),
      icon: markRaw(IconCheck2),
      $isDisabled: selectedItems.value.length !== 1,
      type: ViewActionType.SetAsDefault,
    },
    {
      name: `${capitalizeTranslation('actions.export')}`,
      icon: markRaw(IconFileXLS),
      $isDisabled: selectedItems.value.length < 1,
      type: ViewActionType.ExportXlsx,
    },
    {
      name: capitalizeTranslation('actions.delete'),
      icon: markRaw(IconDelete),
      $isDisabled: selectedItems.value.length < 1,
      type: ViewActionType.Delete,
    },
  ]);
  const views = computed<UserView[]>(() => tableSettings?.userViews || []);
  const orderedTableItems = computed(() => orderBy(tableItems.value, 'name'));
  const totalCount = computed<number>(() => views.value?.length || 0);

  const columns = computed(() => ({
    isDefault: TableColumn.fromObject({
      key: 'view.default',
      type: TableColumnTypes.boolean,
      label: capitalizeTranslation('names.default'),
    }),
    isApplied: TableColumn.fromObject({
      key: 'view.isApplied',
      type: TableColumnTypes.boolean,
      label: capitalizeTranslation('messages.applied'),
    }),
    name: nameColumn.value,
    summary: TableColumn.fromObject({
      key: 'summary',
      type: TableColumnTypes.string,
      label: capitalizeTranslation('names.summary'),
    }),
    actions: TableColumn.fromObject({
      key: 'actions',
      type: TableColumnTypes.string,
      label: capitalizeTranslation('names.action', { n: 2 }),
      maxWidthInPixels: 200,
    }),
  }));
  const nameColumn = computed<TableColumn>(() => {
    const name = new EntityProperty({
      label: capitalize(i('names.name')),
      property: 'view.name',
      type: TableColumnTypes.string,
      handleInlineEditUpdateOutsideOfTable: async (entity: ITableItem, value: string) => await rename(entity, value),
      editablePropertyName: 'view.name',
      editableClassName: 'UserView',
      mutation: null,
      isEditableHandler: () => true,
    });

    return name.getTableColumn();
  });

  async function rename(tableItem: ITableItem, newName: string) {
    await tableSettings.userViewService.rename(tableItem.view, newName);
  }

  async function deleteView(tableItem: ITableItem) {
    await tableSettings.userViewService.remove(tableItem.view);
    clearUrlParams();
    clearTooltips();
    await init();
  }

  function applyView(tableItem: ITableItem): void {
    emits('applyView', tableItem.view);
  }

  async function markAsDefault(tableItem: ITableItem): Promise<void> {
    await tableSettings.userViewService.markAsDefault(tableItem.view);
  }

  function clearUrlParams(): void {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.delete(TableSettingKeys.Filters);
    history.replaceState(null, null, `?${queryParams.toString()}`);
  }

  function exportViews(): void {
    const name = `${tableSettings.id}-${i('names.filter', 2)}`;
    const header = [capitalizeTranslation('names.name'), capitalizeTranslation('names.value')];
    const rows = [];
    const cellTypes = [CellType.STRING, CellType.STRING];

    for (const item of selectedItems.value) {
      rows.push([item.view.name, item.view.value]);
    }

    DownloadUtils.downloadAsXlsx(name, rows, cellTypes, header);
  }

  async function dispatchAction(action: IAction) {
    switch (action.type) {
      case ViewActionType.Delete:
        await tableSettings.userViewService.removeList(selectedItems.value.map((item) => item.view));
        await init();
        break;
      case ViewActionType.Apply:
        applyView(selectedItems.value[0]);
        break;
      case ViewActionType.SetAsDefault:
        await markAsDefault(selectedItems.value[0]);
        break;
      case ViewActionType.ExportXlsx:
        exportViews();
        break;
      default:
        throw new Error('Unknown action type');
    }
  }

  async function init() {
    isLoading.value = true;
    tableItems.value = [];
    for await (const view of views.value) {
      try {
        const summary = await view.getSummary(availableFilters);
        tableItems.value.push({ view, summary, id: view.name });
      } catch (error) {
        // if there is something wrong while getting the summary, still push the views to the table
        tableItems.value.push({ view, summary: [], id: view.name });
      }
    }
    isLoading.value = false;

    if (tableItems.value?.length === 0) {
      emits('closed');
    }
  }

  onMounted(async () => {
    await init();
  });
</script>

<style lang="scss" scoped>
  .entity-actions {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 1rem;
  }

  .view-list-table {
    .summary {
      display: flex;
      gap: 0.75rem;

      .badge {
        margin-right: 0;
      }
    }

    .badge {
      margin-right: 1rem;
    }

    .actions {
      display: flex;
      gap: 1rem;
    }
  }
</style>
